import { CommonModule } from '@angular/common';
import { Component, Injector, Input, ViewChild } from '@angular/core';
import { BaseThirdPartyDocLineEditor, LinesEditorComponent } from '../../../../components';
import {
  GetContextMenuItemsParams,
  MenuItemDef,
  CellValueChangedEvent,
  ColDef,
} from '@ag-grid-community/core';
import {
  CustomerCreditNoteDto,
  CustomerCreditNoteService,
  DocLineRole,
  DocLineUpdatedDto,
} from '@digiwall/goliath-documents/proxy';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import { VatType } from '@digiwall/goliath-core/proxy';
import { EMPTY, Observable } from 'rxjs';
import { ObserverQueue } from '@digiwall/core';
import { DocType } from '../../../base-docs/detail/doc-type';

@Component({
  selector: 'document-customer-credit-note-lines-grid',
  templateUrl: './customer-credit-note-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class CustomerCreditNoteLinesEditorComponent extends BaseThirdPartyDocLineEditor<CustomerCreditNoteDto> {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  @Input() set readonly(value) {
    this._readonly = value;
  }

  @Input() set doc(value) {
    this._document = value;
    this.setLanguageCode();
  }

  protected articleLineRole: DocLineRole = DocLineRole.CustomerCreditNoteArticle;
  protected textLineRole: DocLineRole = DocLineRole.CustomerDocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;
  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticleSellableSelectionLookup(term);

  public columnDefs: Array<ColDef> = [];

  constructor(
    localizationService: LocalizationService,
    private customerInvoiceService: CustomerCreditNoteService,
    injector: Injector,
    updateQueue: ObserverQueue,
  ) {
    super(localizationService, customerInvoiceService, injector, updateQueue);

    this.initColDefs();
  }

  private initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.push(
      ...([
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Sales),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Sales),
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
      ] as Array<ColDef>),
    );
    this.markDimensionFieldsAsNonEditable(this.columnDefs);
  }

  override isChapter = line => line.lineRole == this.chapterLineRole;

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this._readonly) return [];
    return this.contextMenuItems(params);
  };

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this.customerInvoiceService.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.customerInvoiceService.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.customerInvoiceService.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
          );
          break;

        case 'unitWeight':
          observable = this.customerInvoiceService.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          let oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({
            update: [oldData],
          });
        },
      });
  };
}
